import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { StatusCount, CompetitionCount, ProgramCount, AlertFilters, DistrictCount, LabelCount } from "../../../types/grants";
import MultiSelectDropdown, { Option } from "../../common/MultiSelectDropdown";
import { STATUS_LABELS } from "../../../constants/grants.constants";
import { normalizeRegion, getRegionLabel } from "../../../constants/regions";
import { useDebounce } from "../../../hooks/useDebounce";
import { formatNaboryCount } from "../../../utils/text.utils";

/** Special filter values for label filtering */
const LABEL_FILTER_LABELED = "__labeled__";
const LABEL_FILTER_UNLABELED = "__unlabeled__";

/**
 * UUID regex for filter sanitization.
 * Matches standard UUID format (8-4-4-4-12 hex digits), case insensitive.
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Sanitize filters before passing to alert modal.
 *
 * Validates and limits filter values according to backend constraints:
 * - Programs: Must be valid UUIDs, max 20 items (from main filter bar)
 * - Regions: Must be strings, max 50 chars each, max 16 items
 *
 * Note: selectedStatuses, searchQuery, and competitionIds are NOT passed to alerts
 * as the AlertFilters model focuses on structural criteria (program, region)
 * rather than temporal states (status) or text search.
 *
 * @param programIds - Array of program UUIDs from main filter bar
 * @param regions - Array of region strings from CallsFilters
 * @returns Sanitized AlertFilters object safe to pass to CreateAlertModal
 *
 * @example
 * const filters = sanitizeFiltersForAlert(
 *   ['523e4567-e89b-12d3-a456-426614174001', 'invalid'],
 *   ['mazowieckie', 'slaskie']
 * );
 * // Returns: { programs: ['523e4567-e89b-12d3-a456-426614174001'], regions: ['mazowieckie', 'slaskie'] }
 */
export function sanitizeFiltersForAlert(
  programIds: string[],
  regions: string[],
  districts: string[] = []
): AlertFilters {
  return {
    programs: programIds
      .filter(id => UUID_REGEX.test(id))
      .slice(0, 20),
    regions: regions
      .filter(r => typeof r === 'string' && r.length <= 50)
      .map(normalizeRegion)
      .slice(0, 16),
    districtSlugs: districts
      .filter(d => typeof d === 'string' && /^[a-z0-9-]+$/.test(d))
      .slice(0, 100),
  };
}

interface CallsFiltersProps {
  /** Program counts for main dropdown (faceted search) */
  programCounts?: ProgramCount[];
  /** Competition counts for main dropdown (faceted search) */
  competitionCounts?: CompetitionCount[];
  statusCounts?: StatusCount[];
  regions?: string[];
  /** District counts from backend (faceted search, KFS) */
  districtCounts?: DistrictCount[];
  /** Selected competition IDs for main dropdown */
  selectedCompetitionIds?: string[];
  selectedStatuses?: string[];
  selectedRegions?: string[];
  /** Selected district slugs for KFS filtering */
  selectedDistricts?: string[];
  searchQuery?: string;
  /** True during initial data load (shows skeleton) */
  isLoading?: boolean;
  /** True during refetch after filter change (shows subtle indicator) */
  isFetching?: boolean;
  /** Callback when program filter changes */
  onProgramChange?: (programIds: string[]) => void;
  /** Callback when competition filter changes */
  onCompetitionChange?: (competitionIds: string[]) => void;
  onStatusChange?: (statuses: string[]) => void;
  onRegionChange?: (regions: string[]) => void;
  /** Callback when district filter changes */
  onDistrictChange?: (districts: string[]) => void;
  onSearchChange?: (query: string) => void;
  onClearFilters?: () => void;
  onAdvancedFilters?: () => void;
  advancedFiltersCount?: number;
  /** Selected program IDs for main dropdown and alert */
  selectedProgramIds?: string[];
  /** Callback when user clicks "Save as alert" button */
  onSaveAsAlert?: (filters: AlertFilters, triggerRef: React.RefObject<HTMLButtonElement | null>) => void;
  /** Whether the user has reached the maximum number of alerts */
  alertLimitReached?: boolean;
  /** Total count of results for ARIA live region (WCAG 4.1.3) */
  totalCount?: number;
  /** Label counts for label filter dropdown (faceted search) */
  labelCounts?: LabelCount[];
  /** Selected label IDs for filtering */
  selectedLabelIds?: string[];
  /** Selected label filter mode ('labeled' or 'unlabeled') */
  selectedLabelFilter?: string;
  /** Callback when label filter changes */
  onLabelFilterChange?: (labelIds: string[], labelFilter: string | undefined) => void;
}

/**
 * Loading skeleton for a single dropdown filter.
 */
const DropdownSkeleton = () => (
  <div className="w-64 h-10 bg-neutral-200 rounded-md animate-pulse" />
);

/**
 * Dropdown-based filters for grant calls list.
 * Provides Program, Competition, Status, Region dropdowns and search input.
 */
const CallsFilters: React.FC<CallsFiltersProps> = ({
  programCounts = [],
  competitionCounts = [],
  statusCounts = [],
  regions = [],
  districtCounts = [],
  selectedCompetitionIds = [],
  selectedStatuses = [],
  selectedRegions = [],
  selectedDistricts = [],
  searchQuery = "",
  isLoading = false,
  isFetching = false,
  onProgramChange,
  onCompetitionChange,
  onStatusChange,
  onRegionChange,
  onDistrictChange,
  onSearchChange,
  onClearFilters,
  onAdvancedFilters,
  advancedFiltersCount,
  selectedProgramIds = [],
  onSaveAsAlert,
  alertLimitReached = false,
  totalCount,
  labelCounts = [],
  selectedLabelIds = [],
  selectedLabelFilter,
  onLabelFilterChange,
}) => {
  // Ref for the "Save as alert" button (for focus management)
  const saveAsAlertButtonRef = useRef<HTMLButtonElement>(null);

  // Determine if alert can be saved (needs at least one filter criteria and limit not reached)
  const hasFilterCriteria = selectedProgramIds.length > 0 || selectedRegions.length > 0 || selectedDistricts.length > 0;
  const canSaveAlert = hasFilterCriteria && !alertLimitReached;

  // Handle "Save as alert" click with debounce protection
  const handleSaveAsAlert = () => {
    if (!onSaveAsAlert || !canSaveAlert) return;
    // Use programIds from main filter bar for alert (not competitionIds)
    const sanitizedFilters = sanitizeFiltersForAlert(selectedProgramIds, selectedRegions, selectedDistricts);
    onSaveAsAlert(sanitizedFilters, saveAsAlertButtonRef);
  };
  // Local state for search input (immediate UI feedback)
  const [searchInput, setSearchInput] = useState(searchQuery);

  // Ref to track if input is focused (to avoid syncing from parent while editing)
  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);

  // Debounce the search input value
  const debouncedSearchInput = useDebounce(searchInput, 400);

  // Sync local state with external searchQuery prop (only when not focused)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setSearchInput(searchQuery);
    }
  }, [searchQuery]);

  // Propagate debounced value to parent
  useEffect(() => {
    if (debouncedSearchInput !== searchQuery) {
      onSearchChange?.(debouncedSearchInput);
    }
  }, [debouncedSearchInput, searchQuery, onSearchChange]);

  // Handle search input change
  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  // Track focus state
  const handleSearchFocus = useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleSearchBlur = useCallback(() => {
    isFocusedRef.current = false;
  }, []);

  const hasActiveFilters =
    selectedProgramIds.length > 0 ||
    selectedCompetitionIds.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedRegions.length > 0 ||
    selectedDistricts.length > 0 ||
    selectedLabelIds.length > 0 ||
    !!selectedLabelFilter ||
    searchQuery ||
    (advancedFiltersCount !== undefined && advancedFiltersCount > 0);

  // Convert programCounts to options for main dropdown
  const programOptions: Option[] = programCounts.map((program) => ({
    value: program.id,
    label: program.name,
    count: program.count,
  }));

  // Convert competitionCounts to options for main dropdown
  const competitionOptions: Option[] = competitionCounts.map((competition) => ({
    value: competition.id,
    label: competition.name,
    count: competition.count,
  }));

  // Convert statusCounts to options
  const statusOptions: Option[] = statusCounts.map((statusItem) => ({
    value: statusItem.status,
    label: STATUS_LABELS[statusItem.status] || statusItem.status,
    count: statusItem.count,
  }));

  // Convert regions to options with Polish display labels
  const regionOptions: Option[] = regions.map((region) => ({
    value: region,
    label: getRegionLabel(region),
  }));

  // Convert districtCounts to options for dropdown (grouped label: "Name (Voivodeship)")
  const districtOptions: Option[] = districtCounts.map((dc) => ({
    value: dc.slug,
    label: dc.name,
    count: dc.count,
  }));

  // Build label filter options: specific labels + "Tylko oznaczone" + "Bez etykiety"
  const labelFilterOptions: Option[] = useMemo(() => {
    const opts: Option[] = labelCounts.map((lc) => ({
      value: lc.id,
      label: lc.name,
      count: lc.count,
    }));
    // Add special options (with separator from label-specific options)
    opts.push({
      value: LABEL_FILTER_LABELED,
      label: "Tylko oznaczone",
      separator: opts.length > 0,
    });
    opts.push({
      value: LABEL_FILTER_UNLABELED,
      label: "Bez etykiety",
    });
    return opts;
  }, [labelCounts]);

  // Convert selected label state to MultiSelectDropdown value format
  const labelFilterSelectedValues = useMemo(() => {
    const values = [...selectedLabelIds];
    if (selectedLabelFilter === "labeled") values.push(LABEL_FILTER_LABELED);
    if (selectedLabelFilter === "unlabeled") values.push(LABEL_FILTER_UNLABELED);
    return values;
  }, [selectedLabelIds, selectedLabelFilter]);

  /**
   * Handle label filter MultiSelectDropdown changes.
   * Implements mutual exclusion:
   * - "Bez etykiety" excludes specific labels and "Tylko oznaczone"
   * - Specific labels and "Tylko oznaczone" exclude "Bez etykiety"
   */
  const handleLabelFilterDropdownChange = useCallback((values: string[]) => {
    if (!onLabelFilterChange) return;

    const hasUnlabeled = values.includes(LABEL_FILTER_UNLABELED);
    const hadUnlabeled = selectedLabelFilter === "unlabeled";
    const hasLabeled = values.includes(LABEL_FILTER_LABELED);
    const specificIds = values.filter(
      (v) => v !== LABEL_FILTER_LABELED && v !== LABEL_FILTER_UNLABELED
    );

    // If user just selected "unlabeled", clear everything else
    if (hasUnlabeled && !hadUnlabeled) {
      onLabelFilterChange([], "unlabeled");
      return;
    }

    // If user selected specific labels or "labeled" while "unlabeled" was active, remove "unlabeled"
    if (hasUnlabeled && (specificIds.length > 0 || hasLabeled)) {
      onLabelFilterChange(
        specificIds,
        hasLabeled ? "labeled" : undefined
      );
      return;
    }

    onLabelFilterChange(
      specificIds,
      hasLabeled ? "labeled" : hasUnlabeled ? "unlabeled" : undefined
    );
  }, [onLabelFilterChange, selectedLabelFilter]);

  return (
    <div
      data-tour="calls-filters"
      className="sticky top-0 bg-white z-10 pb-4 border-b border-neutral-200"
      role="group"
      aria-label="Filtry naborów"
      aria-busy={isLoading || isFetching}
    >
      {/* ARIA live region for screen readers (WCAG 4.1.3 Status Messages) */}
      <div
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {totalCount !== undefined && formatNaboryCount(totalCount)}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        {/* Program multi-select */}
        <div className="w-64 relative">
          {isLoading ? (
            <DropdownSkeleton />
          ) : (
            <>
              <MultiSelectDropdown
                options={programOptions}
                selectedValues={selectedProgramIds}
                onChange={(values) => onProgramChange?.(values)}
                placeholder="Wszystkie programy"
                ariaLabel="Filtruj według programu"
              />
              {isFetching && (
                <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                  <span className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" aria-hidden="true" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Competition multi-select (main filter per spec) */}
        <div className="w-64 relative">
          {isLoading ? (
            <DropdownSkeleton />
          ) : (
            <>
              <MultiSelectDropdown
                options={competitionOptions}
                selectedValues={selectedCompetitionIds}
                onChange={(values) => onCompetitionChange?.(values)}
                placeholder="Wszystkie konkursy"
                ariaLabel="Filtruj według konkursu"
              />
              {isFetching && (
                <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                  <span className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" aria-hidden="true" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Status multi-select */}
        <div className="w-64 relative">
          {isLoading ? (
            <DropdownSkeleton />
          ) : (
            <>
              <MultiSelectDropdown
                options={statusOptions}
                selectedValues={selectedStatuses}
                onChange={(values) => onStatusChange?.(values)}
                placeholder="Wszystkie statusy"
                ariaLabel="Filtruj według statusu"
              />
              {isFetching && (
                <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                  <span className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" aria-hidden="true" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Region multi-select */}
        {(regions.length > 0 || isLoading) && (
          <div className="w-64 relative">
            {isLoading ? (
              <DropdownSkeleton />
            ) : (
              <>
                <MultiSelectDropdown
                  options={regionOptions}
                  selectedValues={selectedRegions}
                  onChange={(values) => onRegionChange?.(values)}
                  placeholder="Wszystkie regiony"
                  ariaLabel="Filtruj według regionu"
                />
                {isFetching && (
                  <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                    <span className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" aria-hidden="true" />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* District multi-select (KFS) */}
        {(districtCounts.length > 0 || selectedDistricts.length > 0) && (
          <div className="w-64 relative">
            {isLoading ? (
              <DropdownSkeleton />
            ) : (
              <>
                <MultiSelectDropdown
                  options={districtOptions}
                  selectedValues={selectedDistricts}
                  onChange={(values) => onDistrictChange?.(values)}
                  placeholder="Wszystkie powiaty"
                  ariaLabel="Filtruj według powiatu"
                />
                {isFetching && (
                  <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                    <span className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" aria-hidden="true" />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Label multi-select filter */}
        {onLabelFilterChange && (
          <div className="w-64 relative">
            {isLoading ? (
              <DropdownSkeleton />
            ) : (
              <>
                <MultiSelectDropdown
                  options={labelFilterOptions}
                  selectedValues={labelFilterSelectedValues}
                  onChange={handleLabelFilterDropdownChange}
                  placeholder="Wszystkie etykiety"
                  ariaLabel="Filtruj według etykiety"
                />
                {isFetching && (
                  <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                    <span className="animate-spin h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full" aria-hidden="true" />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Search input */}
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <input
            ref={inputRef}
            type="text"
            value={searchInput}
            onChange={handleSearchInputChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            placeholder="Szukaj..."
            title="Wyszukuje po tytule, operatorze, projekcie, konkursie i programie"
            className="w-full bg-white border border-neutral-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            aria-label="Szukaj naborów"
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-neutral-400">
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Clear filters button */}
        {hasActiveFilters && onClearFilters && (
          <button
            onClick={onClearFilters}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-md hover:bg-neutral-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Wyczyść wszystkie filtry"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4 mr-1"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Wyczyść
          </button>
        )}

        {/* Advanced filters button */}
        {onAdvancedFilters && (
          <button
            onClick={onAdvancedFilters}
            data-testid="advanced-filters-button"
            data-tour="calls-advanced-filters"
            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              advancedFiltersCount !== undefined && advancedFiltersCount > 0
                ? "text-primary-700 bg-primary-100 border border-primary-300"
                : "text-primary-600 bg-primary-50 hover:bg-primary-100"
            }`}
            aria-label={
              advancedFiltersCount !== undefined && advancedFiltersCount > 0
                ? `Więcej filtrów (${advancedFiltersCount} aktywnych)`
                : "Więcej filtrów"
            }
            title={
              advancedFiltersCount !== undefined && advancedFiltersCount > 0
                ? `${advancedFiltersCount} aktywnych filtrów zaawansowanych`
                : "Więcej filtrów"
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 mr-1"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
              />
            </svg>
            Więcej
            {advancedFiltersCount !== undefined && advancedFiltersCount > 0 && (
              <span
                aria-hidden="true"
                data-testid="advanced-filters-badge"
                className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary-600 rounded-full"
              >
                {advancedFiltersCount}
              </span>
            )}
          </button>
        )}

        {/* Save as alert button */}
        {onSaveAsAlert && (
          <button
            ref={saveAsAlertButtonRef}
            onClick={handleSaveAsAlert}
            disabled={!canSaveAlert}
            data-tour="calls-save-alert"
            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              canSaveAlert
                ? "text-neutral-700 bg-white border border-neutral-300 hover:bg-neutral-50"
                : "text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed"
            }`}
            aria-label={alertLimitReached ? "Osiągnięto maksymalną liczbę alertów" : canSaveAlert ? "Zapisz bieżące wyszukiwanie jako alert" : "Wybierz program, region lub powiat aby zapisać alert"}
            aria-haspopup="dialog"
            aria-disabled={!canSaveAlert}
            title={alertLimitReached ? "Osiągnięto maksymalną liczbę alertów" : canSaveAlert ? "Zapisz jako alert" : "Wybierz program, region lub powiat aby zapisać alert"}
          >
            {/* Bell icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 md:mr-1"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
              />
            </svg>
            {/* Text hidden on mobile */}
            <span className="hidden md:inline">Zapisz jako alert</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default CallsFilters;
